import { ErrorComponentProps, useRouter } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'

import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import Button from '@mui/material/Button'

import { RouterButton } from 'Common/components/links/routerLinks'
import { logErrorToService } from 'Common/utils/logging/log'

import ErrorPageLayout from './ErrorPageLayout'
import ForbiddenPage from './ForbiddenPage'
import RouteNotFoundPage from './RouteNotFoundPage'

const errorStatus = (error: unknown): number | undefined => {
    if (typeof error !== 'object' || error === null) return undefined
    const candidate = error as { status?: unknown; response?: { status?: unknown } }
    const status = candidate.status ?? candidate.response?.status
    return typeof status === 'number' ? status : undefined
}

const buildReference = () => {
    const block = () =>
        Math.floor(Math.random() * 0x10000)
            .toString(16)
            .toUpperCase()
            .padStart(4, '0')
    return `${block()}-${block()}`
}

export default function RouteErrorPage({ error, reset }: Readonly<ErrorComponentProps>) {
    const router = useRouter()
    const status = errorStatus(error)
    const reference = useMemo(buildReference, [error])

    useEffect(() => {
        if (status === 403 || status === 404) return

        const caught = error as Error | undefined
        logErrorToService({
            message: `[${reference}] ${caught?.message ?? 'Unhandled route error'}`,
            stack: caught?.stack ?? '-',
            name: caught?.name ?? 'RouteError',
        })
    }, [error, reference, status])

    if (status === 403) return <ForbiddenPage />
    if (status === 404) return <RouteNotFoundPage />

    const handleRetry = () => {
        router.invalidate()
        reset()
    }

    return (
        <ErrorPageLayout
            status={status ?? 500}
            icon={<WarningAmberIcon />}
            title="Something went wrong on our end"
            description="We hit an unexpected error while loading this page. Try again — if it keeps happening, let us know so we can look into it."
            reference={reference}
            actions={
                <>
                    <Button variant="contained" onClick={handleRetry}>
                        Try again
                    </Button>
                    <RouterButton to="/mywork" variant="outlined">
                        Go to My work
                    </RouterButton>
                </>
            }
        />
    )
}
